import React from "react";
import styled from "styled-components";

// Small pieces of app UI rebuilt in HTML/CSS, meant to float over a lifestyle photo.
// Amounts are illustrative and mirror the app screenshots (2 250 € in, 785 € out, 1 465 € left).

const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 1.125rem 1.25rem;
  box-shadow: 0 16px 40px rgba(10, 10, 11, 0.14), 0 0 0 1px rgba(10, 10, 11, 0.04);
  font-family: var(--fontR);
  font-variant-numeric: tabular-nums;
  color: var(--text-hi);
`;

const Label = styled.p`
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-lo);
`;

const BigAmount = styled.p`
  font-family: var(--fontL);
  font-size: 2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0.125rem 0 0.75rem;
`;

const Track = styled.div`
  height: 6px;
  border-radius: 3px;
  background: var(--paper-2);
  overflow: hidden;

  i {
    display: block;
    height: 100%;
    width: ${(props) => props.$value}%;
    border-radius: 3px;
    background: ${(props) => props.$color || "var(--green)"};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8125rem;
  padding: 0.5rem 0;

  & + & {
    border-top: 1px solid var(--line);
  }

  strong {
    font-weight: 500;
  }

  small {
    display: block;
    font-size: 0.6875rem;
    color: var(--text-lo);
  }
`;

const Dot = styled.span`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${(props) => props.$color};
`;

const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
`;

export const BudgetCard = () => (
  <Card>
    <Label>Reste ce mois-ci</Label>
    <BigAmount>1 465 €</BigAmount>
    <Track $value={35} $color="#3d4f63">
      <i />
    </Track>
    <Row style={{ paddingTop: "0.75rem" }}>
      <span>Revenu</span>
      <strong>2 250 €</strong>
    </Row>
    <Row>
      <span>Dépenses</span>
      <strong>785 €</strong>
    </Row>
  </Card>
);

const Badge = styled.span`
  display: inline-block;
  margin-bottom: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  background: rgba(21, 184, 127, 0.12);
  color: #0c8a5e;
  font-size: 0.6875rem;
  font-weight: 500;
`;

export const CategoriesCard = () => (
  <Card>
    <Badge>Classé automatiquement</Badge>
    <Row>
      <Name>
        <Dot $color="#3d4f63" />
        <div>
          <strong>Loyer</strong>
          <small>Indispensable</small>
        </div>
      </Name>
      <strong>635 €</strong>
    </Row>
    <Row>
      <Name>
        <Dot $color="#e8a33d" />
        <div>
          <strong>Courses</strong>
          <small>Quotidien</small>
        </div>
      </Name>
      <strong>212 €</strong>
    </Row>
    <Row>
      <Name>
        <Dot $color="#ff8a4c" />
        <div>
          <strong>Sorties</strong>
          <small>Loisirs</small>
        </div>
      </Name>
      <strong>86 €</strong>
    </Row>
  </Card>
);

const Goal = styled.div`
  padding: 0.5rem 0 0.625rem;

  & + & {
    border-top: 1px solid var(--line);
    padding-top: 0.75rem;
  }

  div:first-child {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    font-size: 0.8125rem;
    margin-bottom: 0.5rem;
  }

  strong {
    font-weight: 500;
  }

  span {
    color: var(--text-lo);
    white-space: nowrap;
  }
`;

export const GoalsCard = () => (
  <Card>
    <Label>Objectifs</Label>
    <Goal>
      <div>
        <strong>Coussin de sécurité</strong>
        <span>1 850 / 5 000 €</span>
      </div>
      <Track $value={37}>
        <i />
      </Track>
    </Goal>
    <Goal>
      <div>
        <strong>Vacances d'été</strong>
        <span>640 / 1 200 €</span>
      </div>
      <Track $value={53}>
        <i />
      </Track>
    </Goal>
  </Card>
);

const Pyramid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin: 0.625rem 0 0.75rem;

  i {
    display: block;
    height: 18px;
    border-radius: 3px;
    background: var(--paper-2);
  }

  i:nth-child(1) {
    width: 22%;
    clip-path: polygon(50% 0, 100% 100%, 0 100%);
  }
  i:nth-child(2) {
    width: 44%;
    clip-path: polygon(14% 0, 86% 0, 100% 100%, 0 100%);
  }
  i:nth-child(3) {
    width: 66%;
    clip-path: polygon(9% 0, 91% 0, 100% 100%, 0 100%);
    background: #557d70;
  }
  i:nth-child(4) {
    width: 88%;
    clip-path: polygon(7% 0, 93% 0, 100% 100%, 0 100%);
    background: #16211e;
  }
`;

export const PyramidCard = () => (
  <Card>
    <Label>Patrimoine</Label>
    <BigAmount style={{ marginBottom: 0 }}>5 775 €</BigAmount>
    <Pyramid aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </Pyramid>
    <Row>
      <span>Sécurité</span>
      <strong>75 %</strong>
    </Row>
    <Row>
      <span>ETF / Actions</span>
      <strong>25 %</strong>
    </Row>
  </Card>
);
